import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { CardActions } from 'reactApp/modules/creditCard/creditCard.module';
import { CardSelectors } from 'reactApp/modules/creditCard/creditCard.selectors';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { SubscriptionRenewalActions } from 'reactApp/modules/subscriptionRenewal/subscriptionRenewal.module';
import { InformationAlertBanner, InformationBannerType } from 'reactApp/ui/InformationAlertBanner/InformationAlertBanner';
import { formatExpiresDaySpaceMonth } from 'reactApp/utils/formatDate';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './VigboRenewRemindBanner.css';

export interface VigboRenewBannerProps {
    tariff: BillingDataSubscriptionInfo;
    onClick?: () => void;
}

export const VigboRenewRemindBanner = memo<VigboRenewBannerProps>(({ tariff, onClick }) => {
    const dispatch = useDispatch();
    const { card, isLoading, isLoaded } = useSelector(CardSelectors.getCard);

    useEffect(() => {
        sendPaymentGa({
            eventCategory: ECategoryGa.vigbo,
            action: 'show-banner-expired-sub',
        });
    }, []);

    useEffect(() => {
        dispatch(CardActions.loadCardIfNotLoaded());
    }, [dispatch]);

    const title = useMemo(() => {
        const titleDate = formatExpiresDaySpaceMonth(tariff.expires);
        return (
            <>
                <span className={styles.titleDate}>{titleDate}</span> у вас отключится подписка для фотографов
            </>
        );
    }, [tariff]);

    const text = `Включите автопродление — сможете сохранить ${tariff.space.value} и продолжить создавать галереи Vigbo`;

    const handleClick = () => {
        dispatch(
            SubscriptionRenewalActions.addAutoRenew({
                subscription: tariff,
                card,
                onSuccess: () => {
                    snackbarController.showSnackbar({
                        id: 'vigbo-enable-renew',
                        closable: true,
                        text: 'Автопродление включено',
                        type: SnackbarTypes.success,
                    });
                },
            })
        );

        sendPaymentGa({
            eventCategory: ECategoryGa.vigbo,
            action: 'click-banner-expired-sub',
        });

        onClick?.();
    };

    if (!isLoaded || isLoading) {
        return null;
    }

    return (
        <InformationAlertBanner
            title={title}
            content={text}
            primaryButtonTitle="Включить автопродление"
            type={InformationBannerType.info}
            onPrimaryClick={handleClick}
        />
    );
});

VigboRenewRemindBanner.displayName = 'VigboRenewBanner';
